import React from "react";
import {Button, Card, Col, Form, Input, Row, Table, Typography} from "antd";
import {useNavigate} from "react-router-dom";

import {messageType, showMessages} from "../../../helpers/message";
import {useLazyGetProfileQuery, useLazyGetSearchMemberQuery} from "../../../store/api/accountApi";
import {BaseError} from "../../../types/apiTypes";
import {SearchMember, SearchMemberParams} from "../../../types/accountTypes";

import "./MemberSearch.less";

const MemberSearch = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const [loadActiveMember, {data: loadMemberData, isError: loadMemberIsError, error: loadMemberError, isSuccess: loadMemberIsSuccess}] = useLazyGetProfileQuery();
    const [searchMember, {data: searchData, isError: searchIsError, error: searchError, isFetching}] = useLazyGetSearchMemberQuery();

    const searchTerms = React.useRef<SearchMemberParams>({
        page: 1,
        perPage: 10,
        first_name: null,
        last_name: null,
        phone: null,
        company: null,
        email: null,
    });

    React.useEffect(() => {
        if (loadMemberIsSuccess && loadMemberData) {
            showMessages("You've selected member " + loadMemberData?.membership?.account_number + '-' + loadMemberData?.membership?.membership_number, messageType.success);
            navigate('/account/account_info');
        }

        if (loadMemberIsError && loadMemberError) {
            showMessages((loadMemberError as BaseError).data?.message, messageType.error);
        }

        if (searchIsError && searchError) {
            showMessages((searchError as BaseError).data?.message, messageType.error);
        }
   }, [loadMemberData, loadMemberIsSuccess, loadMemberIsError, loadMemberError, searchIsError, searchError, navigate]);

    const handleFinish = (values: SearchMemberParams) => {
        searchTerms.current = values;
        searchMember({...searchTerms.current});
    }

    const pageChange = (page: number, perPage: number) => {
        searchTerms.current.page = page;
        searchTerms.current.perPage = perPage;
        searchMember({...searchTerms.current});
    }

    const paginationAttr = {
        current: searchData?.meta.current_page || 1,
        pageSize: searchData?.meta.per_page || 10,
        total: searchData?.meta.total,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        onChange: pageChange,
        onShowSizeChange: pageChange,
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
    }

    const columns = React.useMemo(() =>  ([
        {
            title: <Typography.Text strong>Account Number</Typography.Text>,
            dataIndex: 'account_membership_number',
            key: 'account_membership_number',
            render: (account_membership_number: string) => {
                return <span className="member-search-account-number-line-item long-text-wrap">{account_membership_number}</span>
            }
        },
        {
            title: <Typography.Text strong>Name</Typography.Text>,
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => {
                return <span className="member-search-name-line-item long-text-wrap">{name}</span>
            }
        },
        {
            title: <Typography.Text strong>Email</Typography.Text>,
            dataIndex: 'email',
            key: 'email',
            render: (email: string) => {
                return <span className="member-search-email-line-item long-text-wrap">{email}</span>
            }
        },
        {
            title: <Typography.Text strong>Phone Number</Typography.Text>,
            dataIndex: 'phone_number',
            key: 'phone_number',
            render: (phone_number: string) => {
                return <span className="member-search-phone-number-line-item long-text-wrap">{phone_number}</span>
            }
        },
        {
            title: <Typography.Text strong>Company</Typography.Text>,
            dataIndex: 'business_name',
            key: 'business_name',
            render: (business_name: string) => {
                return <span className="member-search-business-name-line-item long-text-wrap">{business_name}</span>
            }
        },
        {
            title: <Typography.Text strong>Status</Typography.Text>,
            dataIndex: 'is_applicant',
            key: 'is_applicant',
            render: (is_applicant: number) => {
                return <span className="member-search-status-line-item long-text-wrap">{is_applicant === 1 ? 'Applicant' : 'Member'}</span>
            },
        },
        {
            title: <Typography.Text strong>Action</Typography.Text>,
            key: 'profile_id',
            dataIndex: 'profile_id',
            render: (profile_id: number, row: SearchMember) =>
                <Button type="link" key="profile_id" onClick={() => loadActiveMember(profile_id)}>
                    Select
                </Button>
        },
    ]), [loadActiveMember]);

    return (
        <div className="member-search-container">
            <Card title="Member Search">
            <Form
                className="member-search-form"
                labelAlign="left"
                labelCol={{span: 8}}
                wrapperCol={{span: 14}}
                form={form}
                name="member-search"
                onFinish={handleFinish}
            >
                <Row justify="start">
                    <Col xs={24} sm={24} md={24} lg={8}>
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{whitespace: true}]}
                        className="member-search-form-item"
                    >
                        <Input/>
                    </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={8}>
                    <Form.Item
                        name="phone"
                        label="Phone"
                        rules={[{whitespace: true}]}
                        className="member-search-form-item"
                    >
                        <Input/>
                    </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={8}>
                    <Form.Item
                        name="company"
                        label="Company"
                        rules={[{whitespace: true}]}
                        className="member-search-form-item"
                    >
                        <Input/>
                    </Form.Item>
                    </Col>
                </Row>
                <Row justify="start">
                    <Col xs={24} sm={24} md={24} lg={8}>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{whitespace: true}]}
                        className="member-search-form-item"
                    >
                        <Input/>
                    </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={8}>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[{whitespace: true}]}
                        className="member-search-form-item"
                    >
                        <Input/>
                    </Form.Item>
                    </Col>
                </Row>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        Search
                    </Button>
                </Form.Item>
            </Form>
            </Card>
            <>
                <Table
                    rowKey="id"
                    loading={isFetching}
                    dataSource={searchData?.data ?? []}
                    columns={columns}
                    className="member-search-table responsive-table"
                    pagination={paginationAttr}
                />
            </>
        </div>
    );
}

export default MemberSearch;
